import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { fetchUserInfo } from '@api/user';

const UserContext = createContext(undefined);

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Start as loading

  // Fetch user info on component mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const userInfo = await fetchUserInfo();
        if (userInfo.status === 'success') {
          const userData = userInfo.data[0]; // Assuming data is an array
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch {
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const contextValue = useMemo(
    () => ({
      user,
      isAuthenticated,
      isLoading,
    }),
    [user, isAuthenticated, isLoading],
  );

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
}

// Custom hook to consume the UserContext
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
